import React, {useEffect} from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import './index.css';
import Login from './Login';
import VasiAdmin from './VasiAdmin';
import reportWebVitals from './reportWebVitals';
import { UserContext } from './UserContext';
import {getAuth, browserSessionPersistence, getIdTokenResult} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import ViewEval from "./view_eval/ViewEval";
import ViewWebEval from "./view_web_eval/ViewWebEval";
import ViewOrganizations from './organization/view-organizations';
import CreateOrganization from './organization/create-organization';
import EditOrganization from './organization/edit-organization';
import ViewClients from './clients/view-clients';
import ViewStaff from './staff/view-staff';
import ViewStimuli from './stimuli/ViewStimuli';
import ViewStimulusDetail from './stimuli/ViewStimulusDetail';
import ViewStimulusQueue from './stimuli/ViewStimulusQueue';
import AddStimuliWizard from './stimuli/AddStimuliWizard';
import ViewControlSlides from './control_slides/ViewControlSlides';
import CreateControlSlide from './control_slides/CreateControlSlide';
import Releases from './releases/Releases';
import Billing from './billing/Billing';
import ReportQueue from './report_queue/ReportQueue';
import ReportQueueEval from './report_queue/ReportQueueEval';
import { loginRedirectState } from './authRoutes';
import {
  CLOUD_EVAL_ROUTE_PATTERNS,
  CLOUD_EVALS_ROOT,
  LEGACY_CLOUD_EVALS_PATH,
  LegacyCloudEvalRedirect,
} from './view_web_eval/cloudEvalRoutes';

const root = ReactDOM.createRoot(document.getElementById('root'));

const auth = getAuth();
auth.setPersistence(browserSessionPersistence);

// Protected Route wrapper component
function ProtectedRoute({ children, user, loading }) {
  const navigate = useNavigate();
  const location = useLocation();
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>;
  }

  useEffect(() => {
    async function checkAdminClaims() {
      if (user) {
        const idTokenResult = await getIdTokenResult(user);
        if (idTokenResult.claims.admin !== true) {
          navigate('/unauthorized', { replace: true });
        }
      }
    }
    checkAdminClaims();
  }, [navigate, user]);

  if (!user) {
    return <Navigate to="/login" replace state={loginRedirectState(location)} />;
  }

  return children;
}

function Unauthorized() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Access Denied
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          You do not have permission to access this application.
        </p>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <button
            onClick={() => auth.signOut()}
            className="flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [user, loading, error] = useAuthState(auth);
  console.log("Auth state changed", user)
  
  const router = createBrowserRouter([
    {
      path: '/',
      element: <Navigate to={CLOUD_EVALS_ROOT} replace />
    },
    {
      path: '/login',
      element: <Login authenticatedUser={user} />
    },
    {
      path: '/unauthorized',
      element: user ? <Unauthorized /> : <Navigate to="/login" replace />
    },
    {
      path: '/evals',
      element: (
        <ProtectedRoute user={user} loading={loading}>
          <VasiAdmin user={user}>
            <ViewEval user={user} />
          </VasiAdmin>
        </ProtectedRoute>
      )
    },
    ...CLOUD_EVAL_ROUTE_PATTERNS.map((path) => ({
      path,
      element: (
        <ProtectedRoute user={user} loading={loading}>
          <VasiAdmin user={user}>
            <ViewWebEval />
          </VasiAdmin>
        </ProtectedRoute>
      )
    })),
    {
      path: LEGACY_CLOUD_EVALS_PATH,
      element: <LegacyCloudEvalRedirect />
    },
    {
      path: '/organizations',
      element: (
        <ProtectedRoute user={user} loading={loading}>
          <VasiAdmin user={user}>
            <ViewOrganizations />
          </VasiAdmin>
        </ProtectedRoute>
      )
    },
    {
      path: '/create_organization',
      element: (
        <ProtectedRoute user={user} loading={loading}>
          <VasiAdmin user={user}>
            <CreateOrganization />
          </VasiAdmin>
        </ProtectedRoute>
      )
    },
    {
      path: '/edit_organization/:id',
      element: (
        <ProtectedRoute user={user} loading={loading}>
          <VasiAdmin user={user}>
            <EditOrganization />
          </VasiAdmin>
        </ProtectedRoute>
      )
    },
    {
      path: '/organization/:id/clients',
      element: (
        <ProtectedRoute user={user} loading={loading}>
          <VasiAdmin user={user}>
            <ViewClients />
          </VasiAdmin>
        </ProtectedRoute>
      )
    },
    {
      path: '/organization/:id/staff',
      element: (
        <ProtectedRoute user={user} loading={loading}>
          <VasiAdmin user={user}>
            <ViewStaff />
          </VasiAdmin>
        </ProtectedRoute>
      )
    },
    {
      path: '/stimuli',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><ViewStimuli /></VasiAdmin></ProtectedRoute>
    },
    {
      path: '/stimuli/queue',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><ViewStimulusQueue /></VasiAdmin></ProtectedRoute>
    },
    {
      path: '/stimuli/add',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><AddStimuliWizard /></VasiAdmin></ProtectedRoute>
    },
    {
      path: '/stimuli/:hash',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><ViewStimulusDetail /></VasiAdmin></ProtectedRoute>
    },
    {
      path: '/control-slides',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><ViewControlSlides /></VasiAdmin></ProtectedRoute>
    },
    {
      path: '/control-slides/new',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><CreateControlSlide /></VasiAdmin></ProtectedRoute>
    },
    {
      path: '/releases',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><Releases /></VasiAdmin></ProtectedRoute>
    },
    {
      path: '/report',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><ReportQueue /></VasiAdmin></ProtectedRoute>
    },
    {
      path: '/report/:evaluationId',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><ReportQueueEval /></VasiAdmin></ProtectedRoute>
    },
    {
      path: '/billing',
      element: <ProtectedRoute user={user} loading={loading}><VasiAdmin user={user}><Billing /></VasiAdmin></ProtectedRoute>
    }
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <UserContext.Provider value={user}>
      <RouterProvider router={router} />
    </UserContext.Provider>
  );
}

root.render(
  <App />
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
